import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Building2, Search, MapPin, Globe2, Phone, ArrowRight, Lock } from 'lucide-react';
import InstallAppCTA from '../components/InstallAppCTA';
import { COUNTRIES_BY_REGION, REGION_ORDER, POPULAR_COUNTRIES, flagEmoji, getCountryName } from '../lib/countries';

/**
 * Public directory of every clinic on the platform.
 * Lives at /clinics — anyone can browse. Each card links to the clinic's
 * public profile + book-with-this-clinic CTA.
 *
 * Iter 125 — Country list expanded from 10 hard-coded rows to the full
 * ISO-3166 catalogue (see `lib/countries.js`), grouped by region with a
 * "Popular" section at the top so common markets stay one click away.
 */

export default function ClinicDirectory() {
  const nav = useNavigate();
  const [country, setCountry] = useState('');
  const [q, setQ] = useState('');
  const [privacyFirst, setPrivacyFirst] = useState(false);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (country) params.country = country;
    if (q.trim()) params.q = q.trim();
    if (privacyFirst) params.privacy_first_only = true;
    api.get('/public/clinics', { params })
      .then(r => setClinics(r.data))
      .finally(() => setLoading(false));
  }, [country, q, privacyFirst]);

  return (
    <div className="min-h-screen bg-[#FAF6EE]">
      {/* Hero */}
      <header className="bg-[#003D80] text-white py-12 md:py-20 px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="text-xs uppercase tracking-[0.2em] text-[#FFB000] font-medium">PHYSIOGENIC NETWORK</Link>
          <h1 className="text-white text-4xl md:text-5xl font-heading font-bold mt-3 max-w-3xl leading-tight">
            Find a Physiogenic-powered clinic near you.
          </h1>
          <p className="text-white/80 mt-3 max-w-2xl text-base">
            Every clinic in our network runs on the same secure platform — physiotherapy, osteopathy, chiropractic, massage and wellness all under one chart.
            HIPAA + GDPR compliant, same convenient booking, same digital records, same body-chart analytics across every visit.
          </p>
        </div>
      </header>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-8">
        <div className="flex flex-col md:flex-row gap-3 mb-8">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted" />
            <input
              className="input pl-9 w-full"
              placeholder="Search by clinic name, city, or address…"
              value={q}
              onChange={e => setQ(e.target.value)}
              data-testid="dir-search"
            />
          </div>
          <select
            className="input md:w-56"
            value={country}
            onChange={e => setCountry(e.target.value)}
            data-testid="dir-country"
          >
            <option value="">All countries</option>
            <optgroup label="Popular">
              {POPULAR_COUNTRIES.map(c => (
                <option key={'pop-' + c.code} value={c.code}>{flagEmoji(c.code)} {c.name}</option>
              ))}
            </optgroup>
            {REGION_ORDER.map(region => (
              <optgroup key={region} label={region}>
                {(COUNTRIES_BY_REGION[region] || []).map(c => (
                  <option key={c.code} value={c.code}>{flagEmoji(c.code)} {c.name}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <label className="inline-flex items-center gap-2 px-3 py-2 border border-soft rounded-md cursor-pointer hover:border-brand transition" data-testid="dir-pf-filter-label">
            <input type="checkbox" checked={privacyFirst} onChange={e => setPrivacyFirst(e.target.checked)} data-testid="dir-pf-filter"/>
            <Lock className="w-4 h-4 text-[#1B5E20]"/>
            <span className="text-sm font-medium">Privacy-First only</span>
          </label>
        </div>

        {/* Results */}
        {loading ? (
          <div className="text-center py-20 text-fg-muted">Loading clinics…</div>
        ) : !clinics.length ? (
          <div className="surface border border-soft rounded-xl p-12 text-center">
            <Building2 className="w-12 h-12 mx-auto text-fg-muted" />
            <h3 className="font-heading font-semibold mt-4">No clinics match your search</h3>
            <p className="text-sm text-fg-muted mt-1">Try a different country or search term.</p>
            <Link to="/start-clinic" className="btn-primary text-sm inline-flex items-center gap-2 mt-6">
              Start your own clinic <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <>
            <div className="text-sm text-fg-muted mb-4">
              <strong>{clinics.length}</strong> clinic{clinics.length !== 1 ? 's' : ''} found
              {country ? ` in ${flagEmoji(country)} ${getCountryName(country, country)}` : ''}.
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clinics.map(c => (
                <div key={c.clinic_id} className="surface border border-soft rounded-xl p-5 hover:border-brand transition flex flex-col" data-testid={`dir-clinic-${c.slug || c.clinic_id}`}>
                  <div className="flex items-center gap-3 mb-3">
                    {c.custom_logo_url
                      ? <img src={c.custom_logo_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                      : <div className="w-12 h-12 rounded-lg bg-brand/10 flex items-center justify-center text-brand"><Building2 className="w-5 h-5" /></div>}
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-semibold truncate">{c.name}</div>
                      <div className="text-[11px] text-fg-muted uppercase tracking-wider">{c.country_code}</div>
                    </div>
                    {c.privacy_first_earned && (
                      <span className="inline-flex items-center gap-1 bg-[#1B5E20]/10 border border-[#5DBB66]/40 text-[#1B5E20] text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
                            title={`Privacy-First — GDPR score ${c.privacy_first_score}/100`}
                            data-testid={`pf-badge-${c.slug || c.clinic_id}`}>
                        <Lock className="w-2.5 h-2.5"/> PF
                      </span>
                    )}
                  </div>
                  {c.custom_tagline && <p className="text-sm text-fg-muted italic mb-3 line-clamp-2 flex-1">"{c.custom_tagline}"</p>}
                  <div className="space-y-1 text-xs text-fg-muted">
                    {c.address && <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3 shrink-0" /> {c.address}</div>}
                    {c.phone && <div className="flex items-center gap-1.5"><Phone className="w-3 h-3 shrink-0" /> {c.phone}</div>}
                    {c.website && <div className="flex items-center gap-1.5"><Globe2 className="w-3 h-3 shrink-0" /> <a href={c.website} target="_blank" rel="noreferrer" className="hover:underline truncate">{c.website.replace(/^https?:\/\//, '')}</a></div>}
                  </div>
                  <button
                    onClick={() => nav(`/c/${c.slug}`)}
                    className="mt-4 btn-primary text-sm w-full inline-flex items-center justify-center gap-2"
                    data-testid={`book-clinic-${c.clinic_id}`}
                  >
                    Book with this clinic <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Join-the-network CTA */}
        <div className="mt-16 surface border border-soft rounded-2xl p-8 md:p-10 bg-gradient-to-br from-[#003D80]/5 to-[#FFB000]/5 text-center">
          <Building2 className="w-10 h-10 mx-auto text-brand" />
          <h2 className="text-2xl md:text-3xl font-heading font-bold mt-3">Run a clinic? Get listed here.</h2>
          <p className="text-fg-muted max-w-xl mx-auto mt-2 text-sm">
            Join the Physiogenic network in 60 seconds — your clinic appears in this directory automatically and you get a full booking + records system.
          </p>
          <Link to="/start-clinic" className="btn-primary text-sm inline-flex items-center gap-2 mt-6">
            Start your clinic free <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <InstallAppCTA variant="multi"/>

      {/* Footer link back to landing */}
      <footer className="text-center py-8 text-xs text-fg-muted">
        <Link to="/" className="hover:text-brand">← Back to Physiogenic</Link>
      </footer>
    </div>
  );
}
